/**
 * A1-B3: Built-in extension templates.
 *
 * Checksums are computed by the platform factory and filled in at runtime.
 */
const kind = (k) => k;
const category = (c) => c;
const mode = (m) => m;
const risk = (r) => r;
const surfaces = (...s) => s;
function builtIn(extensionId, kindVal, name, description, categoryVal, riskVal, modeVal, approval, supports) {
    return {
        extensionId,
        kind: kindVal,
        name,
        description,
        category: categoryVal,
        version: "0.1.0",
        publisher: "Smart Shikka",
        publisherTrusted: true,
        requiredScopes: [],
        risk: riskVal,
        defaultMode: modeVal,
        approvalRequired: approval,
        maxCost: 0,
        maxDuration: 30000,
        enabledByDefault: modeVal !== "denied",
        imported: false,
        supportsSurfaces: surfaces(...supports),
    };
}
function curatedPack(extensionId, name, description, categoryVal, riskVal, supports, enabledByDefault, provenance, conflictMetadata, instructionsPath) {
    return {
        extensionId,
        kind: kind("skill"),
        name,
        description,
        category: categoryVal,
        version: "1.0.0",
        publisher: "Smart Shikka",
        publisherTrusted: false,
        source: provenance.sourceRepository,
        instructionsPath,
        requiredScopes: ["read", "propose"],
        risk: riskVal,
        defaultMode: mode("propose_only"),
        approvalRequired: false,
        maxCost: 0,
        maxDuration: 30000,
        enabledByDefault,
        imported: false,
        supportsSurfaces: surfaces(...supports),
        provenance,
        conflictMetadata,
    };
}
export const BUILTIN_EXTENSION_TEMPLATES = [
    builtIn("builtin-skill-code-generation", kind("skill"), "Code Generation", "Draft code generation preview.", category("coding"), risk("medium"), mode("propose_only"), true, ["code"]),
    builtIn("builtin-skill-code-review", kind("skill"), "Code Review", "Security and style review preview.", category("security"), risk("medium"), mode("propose_only"), true, ["code"]),
    builtIn("builtin-skill-test-generation", kind("skill"), "Test Generation", "Draft test plan preview.", category("testing"), risk("low"), mode("propose_only"), true, ["code"]),
    builtIn("builtin-skill-ui-design", kind("skill"), "UI Design", "Design draft and component suggestions.", category("design"), risk("low"), mode("propose_only"), false, ["cowork"]),
    builtIn("builtin-skill-browser-automation", kind("skill"), "Browser Automation", "Browser action plan preview only.", category("browser_automation"), risk("high"), mode("approval_required"), true, ["shikka_ceo"]),
    builtIn("builtin-skill-document-extraction", kind("skill"), "Document Extraction", "PDF/DOCX/XLSX/PPTX metadata preview.", category("document"), risk("medium"), mode("read_only"), false, ["chat", "cowork"]),
    builtIn("builtin-skill-spreadsheet-analysis", kind("skill"), "Spreadsheet Analysis", "Spreadsheet structure and cell reference preview.", category("spreadsheets"), risk("low"), mode("read_only"), false, ["cowork"]),
    builtIn("builtin-skill-git-commands", kind("skill"), "Git Commands", "Git status, diff and branch plan preview.", category("git"), risk("medium"), mode("propose_only"), true, ["code"]),
    builtIn("builtin-skill-git-github", kind("skill"), "GitHub", "GitHub PR and issue plan preview.", category("github"), risk("medium"), mode("propose_only"), true, ["code"]),
    builtIn("builtin-skill-business-workflow", kind("skill"), "Business Workflow", "Workflow plan draft with approval gate.", category("business_workflows"), risk("medium"), mode("approval_required"), true, ["cowork", "shikka_ceo"]),
    builtIn("builtin-skill-research", kind("skill"), "Research", "Research summary preview from provided context.", category("research"), risk("low"), mode("read_only"), false, ["chat"]),
    builtIn("builtin-skill-architecture", kind("skill"), "Architecture", "System design and dependency review preview.", category("architecture"), risk("medium"), mode("propose_only"), true, ["code"]),
    builtIn("builtin-skill-media-preview", kind("skill"), "Media Preview", "Image/audio/video metadata and transcript hook preview.", category("media"), risk("medium"), mode("read_only"), false, ["chat"]),
    builtIn("builtin-skill-qa", kind("skill"), "QA Review", "Quality assurance checklist preview.", category("qa"), risk("low"), mode("propose_only"), true, ["code"]),
    builtIn("builtin-skill-debugging", kind("skill"), "Debugging", "Error analysis and trace log preview.", category("debugging"), risk("low"), mode("propose_only"), true, ["code"]),
    builtIn("builtin-skill-security-review", kind("skill"), "Security Review", "Security hardening review preview.", category("security"), risk("high"), mode("approval_required"), true, ["code", "shikka_ceo"]),
    builtIn("builtin-tool-system-status", kind("tool"), "System Status Preview", "Static system status preview.", category("general"), risk("low"), mode("read_only"), false, ["chat", "cowork", "code", "shikka_ceo"]),
    builtIn("builtin-tool-text-summary", kind("tool"), "Text Summary Draft", "Draft text summary preview.", category("general"), risk("low"), mode("read_only"), false, ["chat", "cowork"]),
    builtIn("builtin-tool-command-plan", kind("tool"), "Command Plan Preview", "Draft command plan preview.", category("coding"), risk("medium"), mode("propose_only"), true, ["code", "shikka_ceo"]),
    builtIn("builtin-tool-file-write", kind("tool"), "File Write", "File write is permanently denied.", category("coding"), risk("critical"), mode("denied"), true, []),
    builtIn("builtin-tool-command-execute", kind("tool"), "Command Execute", "Command execution is permanently denied.", category("coding"), risk("critical"), mode("denied"), true, []),
    builtIn("builtin-mcp-calculator", kind("mcp"), "Calculator Preview", "Safe numeric formula preview only.", category("general"), risk("low"), mode("read_only"), false, ["chat", "cowork", "code"]),
    builtIn("builtin-mcp-knowledge-search", kind("mcp"), "Knowledge Search Preview", "Sanitized context search preview.", category("research"), risk("low"), mode("read_only"), false, ["chat", "cowork"]),
    // Curated external skill packs. Imported as built-in copies with full provenance.
    curatedPack("smart-shikka-design-pack", "Smart Shikka Design Pack", "Distinctive, intentional visual design guidance curated from Anthropic frontend-design.", category("design"), risk("medium"), ["chat", "cowork", "code"], true, {
        sourceProvider: "Anthropic",
        sourceRepository: "https://github.com/anthropics/skills",
        sourceCommitSha: "f6656c1256d5a8adfa37db9110046ef20bac644c",
        sourcePath: "skills/frontend-design",
        sourceLicense: "Apache-2.0",
        upstreamName: "frontend-design",
        upstreamVersion: undefined,
        attribution: "Adapted from Anthropic Skills (frontend-design) under Apache-2.0.",
        importedAt: "2026-08-17T00:00:00Z",
        reviewedAt: "2026-08-17T00:00:00Z",
    }, { advisoryOnly: true, preferredFor: ["design", "ui", "frontend"] }, "src/skills/curated/design/SKILL.md"),
    curatedPack("smart-shikka-engineering-pack", "Smart Shikka Engineering Pack", "Superpowers engineering methodology — brainstorm, plan, debug, test, review and ship.", category("coding"), risk("medium"), ["chat", "cowork", "code"], true, {
        sourceProvider: "Jesse Vincent / Superpowers",
        sourceRepository: "https://github.com/obra/superpowers",
        sourceCommitSha: "b36e0829c6d0140e93cfef2ca599b1b07d4a7797",
        sourcePath: "skills",
        sourceLicense: "MIT",
        upstreamName: "superpowers",
        upstreamVersion: "v6.3.0",
        attribution: "Adapted from Superpowers under MIT License (Copyright 2025 Jesse Vincent).",
        importedAt: "2026-08-17T00:00:00Z",
        reviewedAt: "2026-08-17T00:00:00Z",
    }, { advisoryOnly: true, preferredFor: ["coding", "planning", "debugging", "review"] }, "src/skills/curated/engineering/SKILL.md"),
    curatedPack("smart-shikka-project-pack", "Smart Shikka Project Pack", "Open GSD project management methodology — milestones, phases and bounded context.", category("business_workflows"), risk("medium"), ["chat", "cowork"], false, {
        sourceProvider: "Open GSD",
        sourceRepository: "https://github.com/open-gsd/gsd-core",
        sourceCommitSha: "8a565957006366ed5e5e231effb7641409a3c654",
        sourcePath: "skills",
        sourceLicense: "MIT",
        upstreamName: "gsd-core",
        upstreamVersion: undefined,
        attribution: "Adapted from Open GSD Core under MIT License (Copyright 2026 Open GSD).",
        importedAt: "2026-08-17T00:00:00Z",
        reviewedAt: "2026-08-17T00:00:00Z",
    }, { advisoryOnly: true, preferredFor: ["project", "milestone", "planning"] }, "src/skills/curated/project/SKILL.md"),
    curatedPack("smart-shikka-leadership-pack", "Smart Shikka Leadership Pack", "gstack leadership routines — reviews, office hours, incident response and release communication.", category("business_workflows"), risk("high"), ["chat", "cowork", "shikka_ceo"], true, {
        sourceProvider: "Garry Tan / gstack",
        sourceRepository: "https://github.com/garrytan/gstack",
        sourceCommitSha: "ae8914af7edaf248f5b0dcd60518d2f6890ad0da",
        sourcePath: ".",
        sourceLicense: "MIT",
        upstreamName: "gstack",
        upstreamVersion: "v1.67.0.0",
        attribution: "Adapted from gstack under MIT License (Copyright 2026 Garry Tan).",
        importedAt: "2026-08-17T00:00:00Z",
        reviewedAt: "2026-08-17T00:00:00Z",
    }, { advisoryOnly: true, preferredFor: ["leadership", "review", "ceo", "incident", "retro"] }, "src/skills/curated/leadership/SKILL.md"),
];
//# sourceMappingURL=constants.js.map